import { useEffect, useCallback } from 'react';
import type { WorkspaceState, WorkspaceCallbacks } from '../../utils/workspace.types';
import { KEYBOARD_SHORTCUTS } from '../../utils/workspace.constants';

/**
 * Workspace keyboard shortcuts hook - handles all keyboard interactions
 * 
 * Supported shortcuts:
 * - L: Toggle labels
 * - G: Toggle grid
 * - N: Toggle node creation mode
 * - B: Toggle beam creation mode
 * - P: Toggle perp mode
 * - T: Toggle analysis panel
 * - Ctrl+C: Copy selected elements
 * - Ctrl+V: Paste with offset dialog
 * - Ctrl+A: Select all nodes and beams
 * - Escape: Clear modes and selections
 * - Delete/Backspace: Delete selected elements
 * 
 * Note: Selection mode is activated via double-click on canvas or ViewMenu buttons
 * 
 * Extracted from STAADWorkspace3D for modularity
 */
export const useWorkspaceKeyboardShortcuts = (
  state: WorkspaceState,
  callbacks: WorkspaceCallbacks,
  operations: {
    nodes: any;
    beams: any;
    selection: any;
  },
  modelStore: any,
  uiStore: any,
  copyPasteStore: any
) => {
  // Handle keyboard events
  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    // Don't handle shortcuts when typing in input fields
    if (event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement) {
      return;
    }

    const key = event.key.toLowerCase();

    switch (key) {
      case KEYBOARD_SHORTCUTS.TOGGLE_LABELS: // 'l'
        callbacks.setShowNodeLabels(!state.showNodeLabels);
        break;
        
      case KEYBOARD_SHORTCUTS.TOGGLE_GRID: // 'g'
        callbacks.setIsGridVisible(!state.isGridVisible);
        break;
        
      case KEYBOARD_SHORTCUTS.NODE_MODE: // 'n'
        callbacks.setIsNodeCreationMode(!state.isNodeCreationMode);
        callbacks.setIsBeamCreationMode(false);
        break;
        
      case KEYBOARD_SHORTCUTS.BEAM_MODE: // 'b'
        callbacks.setIsBeamCreationMode(!state.isBeamCreationMode);
        callbacks.setIsNodeCreationMode(false);
        break;
        
      case KEYBOARD_SHORTCUTS.PERP_MODE: // 'p'
        if (uiStore?.togglePerpMode) {
          uiStore.togglePerpMode();
        }
        break;
        
      // SELECT_MODE ('s') shortcut removed - selection mode is now activated via:
      // 1. Double-click on canvas
      // 2. Clicking Nodes/Beams/All buttons in ViewMenu
        
      case KEYBOARD_SHORTCUTS.TOGGLE_TABLES: // 't'
        callbacks.setShowAnalysisPanel(!state.showAnalysisPanel);
        break;
        
      case 'c':
        if (event.ctrlKey || event.metaKey) {
          event.preventDefault();
          
          if (copyPasteStore?.copySelected) {
            copyPasteStore.copySelected();
            
            if (uiStore?.showNotification) {
              const totalSelected = (modelStore?.selectedNodeIds?.length || 0) + (modelStore?.selectedBeamIds?.length || 0);
              if (totalSelected > 0) {
                uiStore.showNotification(
                  `${totalSelected} element${totalSelected !== 1 ? 's' : ''} copied to clipboard`,
                  'success'
                );
              } else {
                uiStore.showNotification('No elements selected to copy', 'warning');
              }
            }
          }
        }
        break;
        
      case 'v':
        if (event.ctrlKey || event.metaKey) {
          event.preventDefault();
          if (copyPasteStore?.showOffsetDialog) {
            copyPasteStore.showOffsetDialog();
          }
        }
        break;
      
      case 'a':
        // Ctrl+A: Select all nodes and beams
        if (event.ctrlKey || event.metaKey) {
          event.preventDefault();
          
          const allNodeIds = modelStore?.nodes?.map((n: any) => n.id) || [];
          const allBeamIds = modelStore?.beams?.map((b: any) => b.id) || [];
          
          if (modelStore?.selectMultipleNodes && modelStore?.selectMultipleBeams) {
            modelStore.selectMultipleNodes(allNodeIds);
            modelStore.selectMultipleBeams(allBeamIds);
            
            if (uiStore?.showNotification) {
              uiStore.showNotification(
                `Selected all: ${allNodeIds.length} nodes, ${allBeamIds.length} beams`,
                'success'
              );
            }
          }
        }
        break;
        
      case 'escape':
        // Handle escape key - clear modes and selections
        if (state.showQuickNodeDialog) {
          operations.nodes?.handleQuickNodeCancel();
        }
        if (uiStore?.isPerpModeActive && uiStore?.clearPerpSelections && uiStore?.togglePerpMode) {
          uiStore.clearPerpSelections();
          uiStore.togglePerpMode();
        }
        // Exit node creation mode
        if (state.isNodeCreationMode) {
          callbacks.setIsNodeCreationMode(false);
        }
        // Exit beam creation mode
        if (state.isBeamCreationMode) {
          callbacks.setIsBeamCreationMode(false);
        }
        // Exit selection mode and notify ViewMenu
        if (state.isSelectionMode) {
          callbacks.setIsSelectionMode(false);
          window.dispatchEvent(new CustomEvent('selectionModeDeactivated'));
        }
        // Cancel prep mode if active
        if (modelStore?.isPrepMode && modelStore?.cancelPrepMode) {
          modelStore.cancelPrepMode();
        }
        // Cancel beam creation in model store
        if (modelStore?.isBeamCreationMode && modelStore?.cancelBeamCreation) {
          modelStore.cancelBeamCreation();
        }
        // Clear all selections (nodes, beams, pre-beams)
        if (modelStore?.clearSelection) {
          modelStore.clearSelection();
        }
        break;
        
      case 'delete':
      case 'backspace':
        // Delete selected elements
        if (modelStore?.selectedNodeIds?.length > 0) {
          modelStore.selectedNodeIds.forEach((nodeId: string) => {
            if (modelStore.deleteNode) {
              modelStore.deleteNode(nodeId);
            }
          });
        }
        if (modelStore?.selectedBeamIds?.length > 0) {
          modelStore.selectedBeamIds.forEach((beamId: string) => {
            if (modelStore.deleteBeam) {
              modelStore.deleteBeam(beamId);
            }
          });
        }
        break;
        
      case 'enter':
        // No global enter action needed
        break;
    }
  }, [state, callbacks, operations, modelStore, uiStore, copyPasteStore]);

  // Handle key up events for modifier keys
  const handleKeyUp = useCallback((event: KeyboardEvent) => {
    if (event.key === 'Shift') {
      callbacks.setIsShiftPressed(false);
    }
    if (event.key === 'Control' || event.metaKey) {
      callbacks.setIsCtrlPressed(false);
    }
  }, [callbacks]);

  // Handle key down events for modifier keys
  const handleKeyDownModifiers = useCallback((event: KeyboardEvent) => {
    if (event.key === 'Shift') {
      callbacks.setIsShiftPressed(true);
    }
    if (event.key === 'Control' || event.metaKey) {
      callbacks.setIsCtrlPressed(true);
    }
  }, [callbacks]);

  // Set up keyboard event listeners
  useEffect(() => {
    const handleKeyDownCombined = (event: KeyboardEvent) => {
      handleKeyDownModifiers(event);
      handleKeyDown(event);
    };
    
    window.addEventListener('keydown', handleKeyDownCombined);
    window.addEventListener('keyup', handleKeyUp);
    
    return () => {
      window.removeEventListener('keydown', handleKeyDownCombined);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [handleKeyDown, handleKeyUp, handleKeyDownModifiers]);

  return {
    handleKeyDown,
    handleKeyUp,
    handleKeyDownModifiers
  };
};
